// constants
import * as timeSlots from "@clockwise/client-commons/src/constants/time-slot";
// util
import { getAttendeesText, IPerson } from "@clockwise/client-commons/src/util/scheduling-slot";
import {
  calculateDayOffsetTimeSlotFromIndex,
  calculateDayOffsetTimeSlotIndex,
  getAutopilotTimeOfDayFlexibilitySummaryText,
  getEventMoveRangeMinMaxRange,
} from "@clockwise/client-commons/src/util/time-slot";
import { ZonedMoment } from "@clockwise/client-commons/src/util/ZonedMoment";
import { Error, Info } from "@clockwise/design-system/icons";
// constants
import { track, TrackingEvents } from "#clockwise/web-commons/src/util/analytics.util";
import { DayOffsetTimeSlot, Event, PersonListErrorable } from "@clockwise/schema";
import { makeStyles, Tooltip } from "@material-ui/core";
// styles
import { getValue } from "@clockwise/client-commons/src/util/errorable.util";
import { greys } from "../../../styles/color.styles";
import { CalloutCard } from "../../callout-card";
// internal
import { isEqual } from "lodash";
import * as React from "react";
import { openBillingPage, openPricingPage } from "../../../util/upgradeLinks.util";
import { AutopilotFlexibilityOrg } from "../AutopilotFlexibility.types";
import { styles } from "./AutopilotTimeOfDaySlider.styles";

const useStyles = makeStyles(styles);

export interface ThumbState {
  value: number;
  active: boolean;
}

interface AttendeePeopleErrorable {
  attendeePeople: PersonListErrorable;
}

export type TimeOfDaySliderTimeSlot = Omit<DayOffsetTimeSlot, "__typename"> | null;

export interface IProps {
  event: (Event & AttendeePeopleErrorable) | null;
  org: AutopilotFlexibilityOrg | null;
  onChange?: (
    allowedStartTime: TimeOfDaySliderTimeSlot,
    allowedEndTime: TimeOfDaySliderTimeSlot,
  ) => void;
  selectedDate?: string;
  allowedStart?: TimeOfDaySliderTimeSlot;
  allowedEnd?: TimeOfDaySliderTimeSlot;
  muted?: boolean;
  schedulingNewEvent?: boolean;
  isExtension?: boolean;
  attendeeMeetingHoursIntersect?: boolean;
  showJoinExistingPlan?: boolean;
}

export const AutopilotTimeOfDaySlider = ({
  event,
  org,
  allowedStart,
  allowedEnd,
  selectedDate,
  muted,
  onChange,
  schedulingNewEvent,
  isExtension,
  attendeeMeetingHoursIntersect,
  showJoinExistingPlan,
}: IProps) => {
  const classes = useStyles();

  const grid = 1; // Idk what this is for
  const [thumbs, setThumbs] = React.useState<ThumbState[]>([
    { value: 0, active: false },
    { value: timeSlots.maxSlotMaxIndex, active: false },
  ]);
  const [min, setMin] = React.useState(0);
  const [max, setMax] = React.useState(timeSlots.maxSlotMaxIndex);
  const sliderRef = React.useRef<HTMLDivElement | null>(null);

  const attendeeIntersectionHours = getValue(event?.suggestedAutopilotSettingsResponseV3)
    ?.intersectionOfAttendeesMeetingHours;

  const [minPossibleStart, maxPossibleEnd] = getEventMoveRangeMinMaxRange(
    attendeeIntersectionHours,
    org?.userWorkingHours?.workingHours || undefined,
    selectedDate,
  );

  // Tracks the movement of the active thumb, and sets thumbs, min, and max state accordingly.
  const onMouseMove = (e: MouseEvent) => {
    if (e.button !== 0) {
      return;
    }

    const thumbIndex = thumbs.findIndex((thumb) => thumb.active);
    if (thumbIndex !== -1 && sliderRef?.current) {
      const rect = sliderRef?.current?.getBoundingClientRect();

      let nextValue = ((e.clientX - rect.left) / rect.width) * (max - min) + min;
      nextValue = Math.max(
        min,
        Math.min(
          max,
          nextValue - (nextValue % grid) + Math.round((nextValue % grid) * grid) * grid, // With grid = 1 this is always just nextValue
        ),
      );

      if (nextValue !== thumbs[thumbIndex].value) {
        const newThumb = thumbs[thumbIndex];
        newThumb.value = nextValue;

        const minValue = Math.min(thumbs[0].value, thumbs[1].value);
        const maxValue = Math.max(thumbs[0].value, thumbs[1].value);

        const newThumbs = [...thumbs];
        newThumbs[thumbIndex] = newThumb;
        setThumbs(newThumbs);

        setMin(Math.min(Math.max(0, minValue - 2), Math.max(0, minPossibleStart - 2)));
        setMax(
          Math.max(
            Math.min(timeSlots.maxSlotMaxIndex, maxValue + 2),
            Math.min(timeSlots.maxSlotMaxIndex, maxPossibleEnd + 2),
          ),
        );
      }
    }
  };

  // If any thumb is active, and the value of that thumb has changed from allowedStart or allowedEnd props,
  // fire onChange to update the event's move range.
  const onMouseUp = (e: MouseEvent) => {
    if (e.button !== 0) {
      return;
    }

    let wasActive = false;

    // Set active back to false, and check if either were active on this mouseUp.
    const nextThumbs = thumbs.map(({ value, active }) => {
      wasActive = wasActive || active;

      return {
        value,
        active: false,
      };
    });

    const nextStartTimeSlotIndex = Math.min(nextThumbs[0].value, nextThumbs[1].value);
    const nextEndTimeSlotIndex = Math.max(nextThumbs[0].value, nextThumbs[1].value);

    const nextAllowedStart =
      nextStartTimeSlotIndex === minPossibleStart
        ? null
        : calculateDayOffsetTimeSlotFromIndex(nextStartTimeSlotIndex);
    const nextAllowedEnd =
      nextEndTimeSlotIndex === maxPossibleEnd
        ? null
        : calculateDayOffsetTimeSlotFromIndex(nextEndTimeSlotIndex);

    // Check if we changed anything, if so, send it on up.
    if (
      onChange &&
      wasActive &&
      (!isEqual(nextAllowedStart, allowedStart) || !isEqual(nextAllowedEnd, allowedEnd))
    ) {
      onChange(nextAllowedStart, nextAllowedEnd);
    }

    setThumbs(nextThumbs);
  };

  React.useEffect(() => {
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);

    return function cleanup() {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };
  });

  // This is our initialization as well as our willReceiveProps. Updates the thumbs based on
  // the event's move range if set, else the attendees' working hours.
  React.useEffect(() => {
    // Check the existing move range on the event and set or update the current thumbs.
    const allowedStartSlot = calculateDayOffsetTimeSlotIndex(allowedStart);
    const allowedEndSlot = calculateDayOffsetTimeSlotIndex(allowedEnd);

    // Use the move ranges set on the event, else fallback to the min and max constraints.
    const nextMinSlot = allowedStartSlot ?? minPossibleStart;
    const nextMaxSlot = allowedEndSlot ?? maxPossibleEnd;

    const currentMinSlot = Math.min(thumbs[0].value, thumbs[1].value);
    const currentMaxSlot = Math.max(thumbs[0].value, thumbs[1].value);

    // Active is true if either of the thumbs are currently being dragged by the user.
    const draggingHappening = thumbs[0].active || thumbs[1].active;

    if ((nextMinSlot !== currentMinSlot || nextMaxSlot !== currentMaxSlot) && !draggingHappening) {
      const newThumbs = [...thumbs];
      newThumbs[0].value = nextMinSlot;
      newThumbs[1].value = nextMaxSlot;

      setThumbs(newThumbs);
    }

    setMin(Math.min(Math.max(0, nextMinSlot - 2), Math.max(0, minPossibleStart - 2)));
    setMax(
      Math.max(
        Math.min(timeSlots.maxSlotMaxIndex, nextMaxSlot + 2),
        Math.min(timeSlots.maxSlotMaxIndex, maxPossibleEnd + 2),
      ),
    );
  }, [allowedStart, allowedEnd, selectedDate]);

  if (!schedulingNewEvent && (!event || !org)) {
    return null; // Still loading
  }

  /////////////
  // Helpers //
  /////////////
  const onClickViewPlans = () => {
    openPricingPage();
  };

  const onClickViewBilling = () => {
    track(TrackingEvents.FLEXIBLE_MEETING.ADJUST_TIME_OF_DAY.JOIN_PLAN_CLICKED);
    openBillingPage();
  };

  // This is fired upon a mouse down of the slider component track, not the thumbs themselves. It sets the closest thumb to active.
  // When a thumb is active onMouseMove and onMouseUp are engaged.
  const onMouseDownTrack = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.button !== 0) {
      return;
    }

    if (sliderRef?.current) {
      const rect = sliderRef?.current?.getBoundingClientRect();

      let value = ((e.clientX - rect.left) / rect.width) * (max - min) + min;
      value = value - (value % grid);

      const closestThumb = [...thumbs].sort((a, b) => {
        const aDiff = Math.abs(value - a.value);
        const bDiff = Math.abs(value - b.value);
        return aDiff - bDiff;
      })[0];

      closestThumb.value = value;
      closestThumb.active = true;

      const minValue = Math.min(thumbs[0].value, thumbs[1].value);
      const maxValue = Math.max(thumbs[0].value, thumbs[1].value);

      const newMin = Math.min(Math.max(0, minPossibleStart - 2), Math.max(0, minValue - 2), min);
      const newMax = Math.max(
        Math.min(timeSlots.maxSlotMaxIndex, maxPossibleEnd + 2),
        Math.min(timeSlots.maxSlotMaxIndex, maxValue + 2),
        max,
      );

      setMin(newMin);
      setMax(newMax);
    }
  };

  // This tracks the click of a thumb, and sets it to active.
  const onMouseDownThumb = (thumbIndex: number) => (e: React.MouseEvent) => {
    if (e.button !== 0 || muted) {
      return;
    }

    const nextThumbs = thumbs.map(({ value }, i) => ({
      value,
      active: thumbIndex === i,
    }));

    setThumbs(nextThumbs);
  };

  const getThumbStyle = (thumb: ThumbState): React.CSSProperties => {
    return {
      left: `${((thumb.value - min) / (max - min)) * 100}%`,
    };
  };

  const getActiveTrackStyle = (): React.CSSProperties => {
    const sortedThumbs = [...thumbs].sort((a, b) => a.value - b.value);
    const minThumb = sortedThumbs[0];
    const maxThumb = sortedThumbs[1];
    const minRelative = ((minThumb.value - min) / (max - min)) * 100;
    const maxRelative = ((maxThumb.value - min) / (max - min)) * 100;

    return {
      left: `${minRelative}%`,
      right: `${100 - maxRelative}%`,
    };
  };

  const ErrorTracks = () => {
    const bothOutsideSingleConstraint =
      (thumbs[0].value <= minPossibleStart && thumbs[1].value <= minPossibleStart) ||
      (thumbs[0].value >= maxPossibleEnd && thumbs[1].value >= maxPossibleEnd);

    if (bothOutsideSingleConstraint) {
      const minLeft = ((thumbs[0].value - min) / (max - min)) * 100;
      const maxLeft = ((thumbs[1].value - min) / (max - min)) * 100;

      return (
        <div className="track error" style={{ left: `${minLeft}%`, right: `${100 - maxLeft}%` }} />
      );
    }

    return (
      <React.Fragment>
        {thumbs.map((thumb, i) => {
          if (thumb.value < minPossibleStart) {
            const minLeft = ((thumb.value - min) / (max - min)) * 100;
            const maxLeft = ((minPossibleStart - min) / (max - min)) * 100;

            return (
              <div
                key={i}
                className="track error"
                style={{ left: `${minLeft}%`, right: `${100 - maxLeft}%` }}
              />
            );
          } else if (thumb.value > maxPossibleEnd) {
            const minLeft = ((maxPossibleEnd - min) / (max - min)) * 100;
            const maxLeft = ((thumb.value - min) / (max - min)) * 100;

            return (
              <div
                key={i}
                className="track error"
                style={{ left: `${minLeft}%`, right: `${100 - maxLeft}%` }}
              />
            );
          }

          return null;
        })}
      </React.Fragment>
    );
  };

  const StartEndConstraints = () => {
    const minLeft = ((minPossibleStart - min) / (max - min)) * 100;
    const maxLeft = ((maxPossibleEnd - min) / (max - min)) * 100;

    const isMinConstraintBroken =
      thumbs[0].value < minPossibleStart || thumbs[1].value < minPossibleStart;
    const isMaxConstraintBroken =
      thumbs[0].value > maxPossibleEnd || thumbs[1].value > maxPossibleEnd;

    return (
      <React.Fragment>
        <div
          className={classes.constraintMark}
          style={{
            left: `${minLeft}%`,
            backgroundColor: isMinConstraintBroken ? greys.white : undefined,
          }}
        />
        <div
          className={classes.constraintMark}
          style={{
            left: `${maxLeft}%`,
            backgroundColor: isMaxConstraintBroken ? greys.white : undefined,
          }}
        />
      </React.Fragment>
    );
  };

  const NoonMark = () => {
    const minLeft = ((24 - min) / (max - min)) * 100;

    if (minPossibleStart < 24 && maxPossibleEnd > 24) {
      return (
        <div className={classes.noonMark} style={{ left: `${minLeft}%` }}>
          <div className="label">☀️Noon</div>
        </div>
      );
    }

    return null;
  };

  const DisplayInformation = () => {
    const min = Math.min(thumbs[0].value, thumbs[1].value);
    const max = Math.max(thumbs[0].value, thumbs[1].value);
    const isMinDynamic = min === minPossibleStart;
    const isMaxDynamic = max === maxPossibleEnd;
    const isMinOrMaxDynamic = isMinDynamic || isMaxDynamic;
    const minIndex = (min < 0 ? min + timeSlots.all.length : min) % timeSlots.all.length;
    const maxIndex = max % timeSlots.all.length;
    const minSlot = timeSlots.all[minIndex];
    const maxSlot = timeSlots.all[maxIndex];

    const summaryText = getAutopilotTimeOfDayFlexibilitySummaryText(
      minSlot,
      maxSlot,
      isMinDynamic,
      isMaxDynamic,
    );

    const rangeText = `${timeSlots.min[timeSlots.all[minIndex]]} - ${
      timeSlots.max[timeSlots.all[maxIndex]]
    } ${new ZonedMoment().format("z")}`;

    const attendeePeopleMap =
      getValue(event?.attendeePeople)?.list?.reduce(
        (prev: Record<string, IPerson>, curr: IPerson) => {
          prev[curr.primaryEmail] = curr;
          return prev;
        },
        {} as Record<string, IPerson>,
      ) || {};

    const attendeeCalendarIds: string[] = [];
    const attendeePersons: IPerson[] = [];

    getValue(event?.suggestedAutopilotSettingsResponseV3)?.calendarIdToMeetingHours?.forEach(
      ({ calendarId, dayOffsetTimeSlotRanges }) => {
        let withinBounds = false;
        dayOffsetTimeSlotRanges.forEach((dayTimeSlotRange) => {
          // Why is dayOffsetTimeSlotRanges an array?
          const workingHourMin = calculateDayOffsetTimeSlotIndex(dayTimeSlotRange.minSlot);
          const workingHourMax = calculateDayOffsetTimeSlotIndex(dayTimeSlotRange.maxSlot);

          if (
            workingHourMin !== null &&
            workingHourMax !== null &&
            workingHourMin <= min &&
            workingHourMax >= min &&
            workingHourMax >= max &&
            workingHourMin <= max
          ) {
            withinBounds = true;
          }
        });

        if (!withinBounds) {
          if (attendeePeopleMap[calendarId]) {
            attendeePersons.push(attendeePeopleMap[calendarId]);
          }

          attendeeCalendarIds.push(calendarId);
        }
      },
    );

    const { attendeesText } = getAttendeesText(attendeePersons, attendeeCalendarIds);

    if (!attendeeMeetingHoursIntersect) {
      return (
        <div className={classes.displayInformation}>
          <div className="summary outside-hours" cw-id="time-of-day-slider-summary-text">
            {summaryText}
          </div>
          <div className="range outside-hours" cw-id="time-of-day-slider-range-text">
            {rangeText}
            {isMinOrMaxDynamic && (
              <Tooltip
                title="This hour range will update dynamically as any meeting attendees change their meeting hours settings in Clockwise."
                arrow={true}
                placement="top"
              >
                <Info className="error-icon" />
              </Tooltip>
            )}
          </div>
          <div
            className="outside-hours"
            style={{ fontSize: 13, margin: "10px 0px 16px", lineHeight: "20px" }}
          >
            There's no overlap between the meeting hours of this meeting's attendees. Clockwise will
            not move this meeting.
          </div>
        </div>
      );
    }

    return (
      <div className={classes.displayInformation}>
        <div className="summary" cw-id="time-of-day-slider-summary-text">
          {summaryText}
        </div>
        <div className="range" cw-id="time-of-day-slider-range-text">
          {rangeText}
          {isMinOrMaxDynamic && (
            <Tooltip
              title="This hour range will update dynamically as any meeting attendees change their meeting hours settings in Clockwise."
              arrow={true}
              placement="top"
            >
              <>
                {/* The <> </> fragment is workaround to the following issue with MUI:
                 * Warning: Failed prop type: Invalid prop `children` supplied to `ForwardRef(Tooltip)`. Expected an element that can hold a ref
                 * see https://github.com/mui/material-ui/issues/31261
                 */}
                <Info className="info-icon" />
              </>
            </Tooltip>
          )}
        </div>
        {attendeesText && (
          <div className="outside-hours">
            <Error className="error-icon" />
            Outside hours for {attendeesText}
          </div>
        )}
      </div>
    );
  };

  const getAction = () => {
    if (showJoinExistingPlan) {
      return { text: "JOIN A PLAN", onClick: onClickViewBilling };
    } else {
      return { text: "VIEW PLANS", onClick: onClickViewPlans };
    }
  };

  const ThumbTooltip = ({ thumb, i }: { thumb: ThumbState; i: number }) => {
    let roundedTimeSlot = thumb.value % timeSlots.all.length;
    roundedTimeSlot =
      roundedTimeSlot < 0 ? roundedTimeSlot + timeSlots.all.length : roundedTimeSlot;

    const isMin = thumb.value === Math.min(thumbs[0].value, thumbs[1].value);
    const isDynamic = thumb.value === (isMin ? minPossibleStart : maxPossibleEnd);

    let title: JSX.Element | string =
      timeSlots[isMin ? "min" : "max"][timeSlots.all[roundedTimeSlot]];
    title = isDynamic ? (
      <React.Fragment>
        {`${title} - ${isMin ? "start" : "end"} of all`}
        <br />
        attendee's meeting hours
      </React.Fragment>
    ) : (
      title
    );

    return (
      <Tooltip title={title} open={thumb.active ? true : undefined} placement="top" arrow={true}>
        <div
          onMouseDown={onMouseDownThumb(i)}
          className={classes.thumb}
          style={getThumbStyle(thumb)}
          cw-id={`${isMin ? "time-of-day-slider-thumb-min" : "time-of-day-slider-thumb-max"}`}
        />
      </Tooltip>
    );
  };

  return (
    <div>
      {muted && !isExtension && (
        <CalloutCard
          sentiment="pro"
          content={{
            body:
              "Upgrade your free plan to customize meeting move range by hours, so that your meetings are always at the best time.",
          }}
          action={getAction()}
        />
      )}
      <div style={{ flex: 1 }} className={muted ? classes.muted : ""}>
        <div ref={sliderRef} className={classes.sliderRoot}>
          <div onMouseDown={onMouseDownTrack} className={classes.trackRoot}>
            <div className="relative-track-root">
              <div className="track" />
              <div
                className={!attendeeMeetingHoursIntersect ? "track error" : "track active"}
                style={getActiveTrackStyle()}
              />
              <ErrorTracks />
            </div>
            <StartEndConstraints />
            <NoonMark />
          </div>
          {thumbs.map((thumb, i) => (
            <ThumbTooltip key={`${i}-${thumb.active}`} thumb={thumb} i={i} />
          ))}
        </div>
        <DisplayInformation />
      </div>
    </div>
  );
};
